import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Scan, Languages, Calendar, MessageCircle, CheckCircle2, Plus, Minus, Sparkles, Smile, Palette, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-2994">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-2995" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-2996">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-2997">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-2998">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-2999">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-3000" /> : <Plus className="w-4 h-4" data-id="element-3001" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-3002">
        <p className="text-gray-600 leading-relaxed" data-id="element-3003">{answer}</p>
      </div>
    </div>;
}
export function VeneersLondonPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Скільки коштують вініри в Лондоні?',
    a: 'Ціна залежить від матеріалу (кераміка чи композит), кількості зубів та складності роботи. Після консультації ми пропонуємо детальний план лікування та кошторис.'
  }, {
    q: 'Чи потрібно обточувати зуби під вініри?',
    a: 'У більшості випадків потрібна мінімальна підготовка емалі (0.3-0.5 мм), але обсяг залежить від клінічної ситуації. Ми завжди обираємо максимально щадний підхід (minimally invasive).'
  }, {
    q: 'Скільки служать вініри?',
    a: 'Керамічні вініри можуть служити 10-15 років і більше при правильному догляді. Термін служби залежить від матеріалу, прикусу та гігієни. На консультації ми пояснюємо, як продовжити довговічність.'
  }, {
    q: 'Чи можна зробити природний результат?',
    a: 'Так, це наша спеціалізація. Ми підбираємо форму, текстуру та колір індивідуально, щоб усмішка виглядала гармонійно і натурально, а не штучно.'
  }, {
    q: 'Яка різниця між порцеляновими та композитними вінірами?',
    a: 'Порцелянові (керамічні) вініри міцніші, довговічніші та стійкіші до фарбування. Композитні вініри (bonding) доступніші за ціною і часто встановлюються за один візит, але потребують частішого полірування.'
  }, {
    q: 'Скільки візитів потрібно для вінірів?',
    a: 'Зазвичай 2-3 візити для керамічних вінірів: консультація, підготовка/відбитки, встановлення. Композитні вініри часто можна зробити за один візит.'
  }, {
    q: 'Чи можна отримати консультацію українською?',
    a: 'Так, у Dentico Clinic можливе спілкування українською мовою під час консультації та лікування.'
  }, {
    q: 'Де знаходиться Dentico Clinic?',
    a: 'Angel / Islington, 360–364 City Road, London EC1V 2PY. 5 хвилин пішки від станції Angel.'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Dental Veneers',
    description: 'Cosmetic dental procedure to improve the appearance of teeth using veneers.',
    procedureType: 'http://schema.org/NoninvasiveProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-3004">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-3005" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-3006" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-3007" />

      {/* 1. HERO SECTION */}
      <section className="relative bg-white overflow-hidden" data-id="element-3008">
        <div className="absolute inset-0 bg-teal-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-3009" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-3010">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3011">
            <div className="space-y-6" data-id="element-3012">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-800 text-sm font-medium" data-id="element-3013">
                <span className="relative flex h-2 w-2" data-id="element-3014">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-3015"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" data-id="element-3016"></span>
                </span>
                Естетична стоматологія
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-3017">
                Вініри у Лондоні –{' '}
                <span className="text-teal-600" data-id="element-3018">Dentico Clinic</span>
                <br data-id="element-3019" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-3020">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-3021">
                Створіть ідеальну посмішку за допомогою керамічних вінірів або
                композитного бондингу. Природний результат, мінімальне
                втручання, консультація українською.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-gray-700" data-id="element-3022">
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3023">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" data-id="element-3024" />
                  Smile Makeover
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3025">
                  <MapPin className="w-4 h-4 text-teal-600" data-id="element-3026" />
                  Angel, EC1V
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3027">
                  <Languages className="w-4 h-4 text-teal-600" data-id="element-3028" />
                  Українська мова
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-3029">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3030">
                  <Button size="lg" className="w-full gap-2 shadow-lg shadow-teal-600/20" data-id="element-3031">
                    <Calendar className="w-5 h-5" data-id="element-3032" />
                    Записатись на консультацію
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3033">
                  <Button variant="outline" size="lg" className="w-full gap-2 border-teal-200 text-teal-700 hover:bg-teal-50" data-id="element-3034">
                    <MessageCircle className="w-5 h-5 text-green-600" data-id="element-3035" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-3036">
              <div className="absolute inset-0 bg-teal-200 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-3037"></div>
              <img src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=1200&q=80" alt="Вініри та естетична стоматологія - Dentico Clinic" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full" data-id="element-3038" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHAT ARE VENEERS */}
      <section className="py-16 bg-white" data-id="element-3039">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3040">
          <div className="text-center mb-10" data-id="element-3041">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3042">
              Що таке вініри?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-3043">
              Вініри — це ультратонкі керамічні накладки, які фіксуються на
              передню поверхню зубів для покращення їх форми, кольору та
              положення. Це ідеальне рішення для корекції сколів, проміжків між
              зубами (діастем), зміни кольору емалі, який не піддається
              відбілюванню, або вирівнювання незначних нерівностей.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mt-4" data-id="element-3044">
              Сучасні вініри виглядають абсолютно природно завдяки здатності
              пропускати світло так само, як натуральна емаль. У Dentico Clinic
              ми використовуємо цифрові технології для моделювання вашої
              майбутньої посмішки ще до початку лікування.
            </p>
          </div>
        </div>
      </section>

      {/* 3. PROCESS */}
      <section className="py-16 bg-gray-50" data-id="element-3045">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3046">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-3047">
            Шлях до нової посмішки
          </h2>
          <div className="grid md:grid-cols-4 gap-8" data-id="element-3048">
            {[{
            step: '01',
            title: 'Консультація',
            desc: 'Обговорення побажань, фотопротокол та цифрове моделювання посмішки.'
          }, {
            step: '02',
            title: 'Планування',
            desc: 'Примірка макету (mock-up), щоб ви побачили результат до початку робіт.'
          }, {
            step: '03',
            title: 'Підготовка',
            desc: 'Мінімальна обробка зубів та зняття точних відбитків або сканування.'
          }, {
            step: '04',
            title: 'Фіксація',
            desc: 'Встановлення готових керамічних вінірів та фінальна естетика.'
          }].map((item, idx) => <Card key={idx} className="p-6 relative overflow-hidden" data-id="element-3049">
                <div className="text-6xl font-bold text-teal-50 absolute -top-4 -right-4 select-none" data-id="element-3050">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-3051">
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-3052">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-3053">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 4. TYPES */}
      <section className="py-16 bg-white" data-id="element-3054">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3055">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3056">
            <div data-id="element-3057">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3058">
                Види естетичної реставрації
              </h2>
              <div className="space-y-6" data-id="element-3059">
                <div className="flex gap-4" data-id="element-3060">
                  <div className="p-3 bg-teal-50 rounded-lg text-teal-600 h-fit" data-id="element-3061">
                    <Sparkles className="w-6 h-6" data-id="element-3062" />
                  </div>
                  <div data-id="element-3063">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-3064">
                      Керамічні вініри
                    </h3>
                    <p className="text-gray-600" data-id="element-3065">
                      Виготовляються в лабораторії з високоміцної кераміки.
                      Максимально естетичні, довговічні та стійкі до зміни
                      кольору.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-3066">
                  <div className="p-3 bg-teal-50 rounded-lg text-teal-600 h-fit" data-id="element-3067">
                    <Palette className="w-6 h-6" data-id="element-3068" />
                  </div>
                  <div data-id="element-3069">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-3070">
                      Композитний бондинг
                    </h3>
                    <p className="text-gray-600" data-id="element-3071">
                      Художня реставрація композитним матеріалом прямо в кріслі.
                      Швидше та доступніше, але потребує періодичного
                      полірування.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-3072">
                  <div className="p-3 bg-teal-50 rounded-lg text-teal-600 h-fit" data-id="element-3073">
                    <Smile className="w-6 h-6" data-id="element-3074" />
                  </div>
                  <div data-id="element-3075">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-3076">
                      Smile Makeover
                    </h3>
                    <p className="text-gray-600" data-id="element-3077">
                      Комплексне перевтілення посмішки, яке може поєднувати
                      вініри, коронки та відбілювання для ідеального результату.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl" data-id="element-3078">
              <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Типи вінірів" className="absolute inset-0 w-full h-full object-cover" data-id="element-3079" />
            </div>
          </div>
        </div>
      </section>

      {/* 5. BENEFITS */}
      <section className="py-16 bg-teal-50" data-id="element-3080">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3081">
          <div className="text-center mb-12" data-id="element-3082">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3083">
              Чому варто обрати Dentico Clinic
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3084">
            {[{
            title: 'Природний вигляд',
            desc: 'Ми створюємо посмішки, які виглядають натурально, а не штучно.'
          }, {
            title: 'Мінімальне втручання',
            desc: 'Зберігаємо максимум здорових тканин зуба завдяки сучасним технікам.'
          }, {
            title: 'Українська мова',
            desc: 'Легке спілкування та повне розуміння ваших побажань.'
          }, {
            title: 'Зручна локація',
            desc: 'Клініка в центрі Лондона (Angel), легко дістатися з будь-якого району.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white text-center" data-id="element-3085">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mb-4 mx-auto" data-id="element-3086">
                  <CheckCircle2 className="w-6 h-6" data-id="element-3087" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-3088">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-3089">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* MID-PAGE CTA */}
      <section className="py-12 bg-teal-600" data-id="element-3090">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-3091">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4" data-id="element-3092">
            Хочете побачити, як виглядатиме ваша нова посмішка?
          </h2>
          <p className="text-teal-100 mb-6" data-id="element-3093">
            Запишіться на консультацію — ми покажемо цифровий макет результату.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-3094">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-3095">
              <Button size="lg" className="gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-3096">
                <Calendar className="w-5 h-5" data-id="element-3097" />
                Записатись онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-3098">
              <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" data-id="element-3099">
                <MessageCircle className="w-5 h-5" data-id="element-3100" />
                WhatsApp
              </Button>
            </a>
            <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" onClick={() => setIsCallbackOpen(true)} data-id="element-3101">
              <Phone className="w-5 h-5" data-id="element-3102" />
              Замовити дзвінок
            </Button>
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-3103">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3104">
          <div className="text-center mb-10" data-id="element-3105">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3106">
              Часті запитання про вініри
            </h2>
          </div>
          <div className="space-y-2" data-id="element-3107">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-3108" />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-3109">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3110">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-3111">
            Дивіться також
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-3112">
            <Link to="/ua/ukrainskyi-stomatolog-london" data-id="element-3113">
              <Button variant="outline" size="sm" data-id="element-3114">
                Український стоматолог
              </Button>
            </Link>
            <Link to="/ua/implanty-london" data-id="element-3115">
              <Button variant="outline" size="sm" data-id="element-3116">
                Імплантація
              </Button>
            </Link>
            <Link to="/ua/root-canal-london" data-id="element-3117">
              <Button variant="outline" size="sm" data-id="element-3118">
                Лікування каналів
              </Button>
            </Link>
            <Link to="/ua/emergency-dentist-london" data-id="element-3119">
              <Button variant="outline" size="sm" data-id="element-3120">
                Невідкладна допомога
              </Button>
            </Link>
            <Link to="/ua/services/smile-makeover" data-id="element-3121">
              <Button variant="outline" size="sm" data-id="element-3122">
                Smile Makeover
              </Button>
            </Link>
            <Link to="/ua/prices" data-id="element-3123">
              <Button variant="outline" size="sm" data-id="element-3124">
                Ціни
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA */}
      <section className="py-16 bg-teal-900" data-id="element-3125">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-3126">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-3127">
            Мрієте про ідеальну посмішку?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-3128">
            Запишіться на консультацію та дізнайтеся, як вініри можуть змінити
            ваше життя.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-3129">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3130">
              <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-3131">
                <Calendar className="w-5 h-5" data-id="element-3132" />
                Записатись онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3133">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-3134">
                <MessageCircle className="w-5 h-5" data-id="element-3135" />
                WhatsApp
              </Button>
            </a>
            <div className="w-full sm:w-auto" data-id="element-3136">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" onClick={() => setIsCallbackOpen(true)} data-id="element-3137">
                <Phone className="w-5 h-5" data-id="element-3138" />
                Замовити дзвінок
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}